import { useState } from 'react';
import { useProductTagging } from '@modules/product-tagging/context';
import { SupplierSearchForm } from '@modules/product-tagging/components/product-tagging-search/SupplierSearchForm';
import { ProductSearchForm } from '@modules/product-tagging/components/product-tagging-search/ProductSearchForm';
import ProductDetailsForm from '@modules/product-tagging/components/product-details-form/ProductDetailsForm';
import type {
    SearchFormType,
    TagDataUseToCreateTag,
} from '@modules/product-tagging/type';
import type { State } from '@popperjs/core';

interface SelectedSupplier {
    id: number;
    title: string;
    link: string;
    offPlatformStub?: boolean;
    websiteURL?: string;
}

interface ProductSearchModalProps {
    onCreateMarker: (data: TagDataUseToCreateTag) => void;
    popperState: State | null;
    /** Called when the user dismisses any modal in the flow without submitting. */
    onDismiss?: () => void;
}

/** Orchestrates the product → supplier → product details flow. */
const ProductTaggingSearchModal = ({
    onCreateMarker,
    popperState,
    onDismiss,
}: ProductSearchModalProps) => {
    const [formType, setFormType] = useState<SearchFormType>('product');
    const [selectedSupplier, setSelectedSupplier] =
        useState<SelectedSupplier | null>(null);
    const { dispatch } = useProductTagging();

    if (formType === 'productDetails' && selectedSupplier) {
        return (
            <ProductDetailsForm
                supplier={selectedSupplier}
                onSubmit={onCreateMarker}
                onDismiss={onDismiss}
            />
        );
    }

    if (formType === 'supplier') {
        return (
            <SupplierSearchForm
                onClickButton={() =>
                    dispatch({
                        type: 'TOGGLE_ADDING_SUPPLIER',
                        payload: true,
                    })
                }
                onSelectSupplier={(supplier) => {
                    setSelectedSupplier(supplier);
                    setFormType('productDetails');
                }}
                onDismiss={onDismiss}
            />
        );
    }

    return (
        <ProductSearchForm
            onFormToggle={(type) => setFormType(type)}
            onConfirm={onCreateMarker}
            popperState={popperState}
            onDismiss={onDismiss}
        />
    );
};

export default ProductTaggingSearchModal;
